/**
 * lib/consent.ts — EU cookie-consent state (shared client + component logic)
 *
 * Single source of truth for the consent decision shape, localStorage
 * persistence (12-month TTL, revision brief 2026-07-15), the reopen event
 * name used by the "Manage cookie preferences" link on /cookie-policy and
 * /privacy-policy, and the small cookie-read helper used to consume the
 * server-computed geo signals middleware.ts writes (see Purpose 8 there).
 *
 * Consent categories — Necessary + Analytics only (Marketing dropped
 * 2026-07-15, Lars turned ad tracking off entirely):
 *   - necessary: always true, not user-controllable (session, CSRF, this
 *     consent choice itself). Exempt from consent under ePrivacy/GDPR.
 *   - analytics: gates GA4, Ahrefs Analytics, and Microsoft Clarity. All
 *     three are treated as one category — none of them loads or sets a
 *     cookie until this is true. GA4's own ad/DoubleClick signals are
 *     disabled outright in app/layout.tsx regardless of this toggle — see
 *     the `allow_google_signals` / `allow_ad_personalization_signals` gtag
 *     config there.
 *
 * No non-essential script anywhere in the app reads this value directly by
 * import — instead it is read once by <ConsentManager /> (components/ConsentManager.tsx),
 * which is the only place that injects/removes third-party script tags and
 * fires Google Consent Mode v2 updates. This keeps "what loads when" in one
 * auditable file.
 *
 * Cycle: EU cookie-consent banner + Clarity install (brief 2026-07-15).
 * Revised 2026-07-15 (same day, Eleanor's second brief): 12-month TTL added,
 * Marketing category dropped, geo cookie-read helper added.
 * SOP refs: SOP-WEBSITE-03, SOP-WEBSITE-04, SOP-BRAND-01, SOP-WEBSITE-05
 * (pattern reference for server-side-only geo detection).
 */

export const CONSENT_STORAGE_KEY = 'pf_consent_v1';
export const CONSENT_REOPEN_EVENT = 'pf-open-consent-manager';
export const CONSENT_CHANGE_EVENT = 'pf-consent-changed';

/** 12 months, per Eleanor's 2026-07-15 revision brief item 3. */
export const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

export interface ConsentState {
  /** Schema version — bump if the decision shape changes, to force a re-prompt. */
  v: 1;
  /** Always true. Kept explicit in the stored shape for forward-compatibility. */
  necessary: true;
  /** Gates GA4 + Ahrefs + Clarity as a single category. */
  analytics: boolean;
  /** ISO timestamp of the decision. */
  decidedAt: string;
  /** ISO timestamp 12 months after decidedAt — the record expires and the
   *  visitor is re-prompted after this point. Stored explicitly (not just
   *  derived from decidedAt + TTL at read time) so the expiry is visible
   *  in the stored record itself, per the brief's "confirm the stored
   *  record carries the expiry" instruction. */
  expiresAt: string;
}

/**
 * Reads the stored consent decision, if any, treating an expired record
 * (decidedAt + 12 months has passed) as if none exists — this is the
 * re-prompt-after-expiry mechanism. Does NOT delete the expired record from
 * localStorage; writeStoredConsent() overwrites it on the next decision.
 */
export function readStoredConsent(): ConsentState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.v !== 1 || typeof parsed.analytics !== 'boolean') {
      return null;
    }
    const expiresAt = typeof parsed.expiresAt === 'string' ? Date.parse(parsed.expiresAt) : NaN;
    if (!Number.isNaN(expiresAt) && Date.now() > expiresAt) {
      return null; // expired — treat as no decision, banner re-prompts
    }
    return parsed as ConsentState;
  } catch {
    return null;
  }
}

export function writeStoredConsent(analytics: boolean): ConsentState {
  const decidedAt = new Date();
  const expiresAt = new Date(decidedAt.getTime() + CONSENT_TTL_MS);
  const state: ConsentState = {
    v: 1,
    necessary: true,
    analytics,
    decidedAt: decidedAt.toISOString(),
    expiresAt: expiresAt.toISOString(),
  };
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // localStorage unavailable (private mode / disabled) — decision still
    // applies for this page view via React state, just won't persist.
  }
  window.dispatchEvent(new CustomEvent(CONSENT_CHANGE_EVENT, { detail: state }));
  return state;
}

/**
 * Reads a single cookie value by name. Used ONLY to consume the
 * server-computed geo/GPC signals middleware.ts writes as response cookies
 * (`pf-consent-required`, `pf-consent-default`) — this is NOT client-side
 * geo detection (no navigator.language / Intl / IP-API calls anywhere in
 * this file); it is reading a value Cloudflare's edge already computed.
 */
export function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.$?*|{}()[\]\\/+^]/g, '\\$&') + '=([^;]*)'));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Marketing pages Clarity is scoped to this cycle — exact-path match only. See brief 2026-07-15.
 *  Expanded 2026-07-16 (production go-live cycle) to add /store/distance and 5 named blog posts. */
export const CLARITY_SCOPED_ROUTES = [
  '/',
  '/pro-member',
  '/super-fingers',
  '/store',
  '/store/distance',
  '/blog/the-magic-of-czerny-etudes-for-the-piano',
  '/blog/19-piano-exercises-for-the-weak-fingers',
  '/blog/5-things-to-become-a-professional-pianist',
  '/blog/piano-technique-of-horowitz',
  '/blog/ukrainian-piano-composers',
];

/**
 * Cookie-name patterns for the three consent-gated analytics tools (GA4,
 * Ahrefs Analytics, Microsoft Clarity). Used by clearAnalyticsCookies()
 * below — see that function's doc comment for when/why this runs.
 */
const ANALYTICS_COOKIE_PATTERNS: RegExp[] = [
  /^_ga$/,       // GA4 client ID
  /^_ga_/,       // GA4 per-measurement-ID session cookie (_ga_<CONTAINER>)
  /^_gid$/,      // legacy UA-style, harmless to also clear if present
  /^_gat/,       // GA rate-limiting cookie variants
  /^_clck$/,     // Microsoft Clarity user ID
  /^_clsk$/,     // Microsoft Clarity session ID
];

/**
 * Genuinely revokes analytics cookies already sitting in the browser — the
 * "reopen the banner and change your mind from granted to denied" case
 * (2026-07-16 button-swap brief, item 3: "Refuse must genuinely disable
 * analytics"). Consent Mode v2's `analytics_storage: denied` (set by
 * updateConsentMode() in ConsentManager.tsx) stops FUTURE hits, but does
 * NOT delete cookies GA4/Ahrefs/Clarity already wrote to disk during an
 * earlier granted session — this function does that half of the job.
 *
 * Called unconditionally whenever commitDecision(false) runs (first-time
 * visitors simply have nothing to clear — the loop below is a no-op for
 * them, cheap and side-effect-free). Tries several domain scopes because
 * gtag.js/Clarity may have written the cookie on the bare hostname
 * (staging.pianofantasy.com), a leading-dot host variant, or the parent
 * eTLD+1 (pianofantasy.com) depending on how the script configured itself
 * — expiring on the wrong domain silently no-ops, so we cover all of them.
 */
export function clearAnalyticsCookies(): void {
  if (typeof document === 'undefined') return;
  const presentNames = document.cookie
    .split(';')
    .map((c) => c.split('=')[0]?.trim())
    .filter((name): name is string => Boolean(name));
  const targets = presentNames.filter((name) => ANALYTICS_COOKIE_PATTERNS.some((re) => re.test(name)));
  if (targets.length === 0) return;

  const host = window.location.hostname; // e.g. staging.pianofantasy.com
  const parts = host.split('.');
  const parentDomain = parts.length > 2 ? parts.slice(-2).join('.') : host; // e.g. pianofantasy.com
  const domainVariants: (string | undefined)[] = [undefined, host, `.${host}`, parentDomain, `.${parentDomain}`];
  const expired = 'Thu, 01 Jan 1970 00:00:00 UTC';

  for (const name of targets) {
    for (const domain of domainVariants) {
      const domainAttr = domain ? `; domain=${domain}` : '';
      document.cookie = `${name}=; expires=${expired}; path=/${domainAttr}`;
    }
  }
}
